import { CategoryRailRowDropProvider } from "@/components/navigation/category-rail-row-drop";
import { useTransitionAcrossPages } from "@/components/navigation/transition-across-pages";
import { classNames } from "@/utils/classnames";
import { MOTION_DURATION_CLASS } from "@/utils/motion";
import { useRouter } from "next/router";
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Whether the category rail's environment icons are pushed off-screen, leaving only the workspace
 * mark. Global settings pages span every project and environment, so the icons, which belong to
 * one environment, make way for the global settings sidebar there. LayoutV2 owns this because the
 * rail, the top rail, and the page pane move together.
 */
export interface CategoryRailCollapse {
  collapsed: boolean;
  /**
   * Moves the rail for a navigation the reader just started, before its page arrives. It moves
   * back if no route change starts or the change fails.
   */
  anticipate: (collapsed: boolean) => void;
  /**
   * The rail is moving for a page that has not arrived yet, so what this page shows of its own
   * place (the top rail's breadcrumbs) is about to change.
   */
  leaving: boolean;
}

const CategoryRailCollapseContext = createContext<CategoryRailCollapse>({
  collapsed: false,
  anticipate: () => {},
  leaving: false,
});

export const useCategoryRailCollapse = () => useContext(CategoryRailCollapseContext);

/** The rail, top rail, and pane share this 200ms ease-out, so they move as one. */
export const CATEGORY_RAIL_COLLAPSE_TRANSITION_CLASS = classNames(
  MOTION_DURATION_CLASS.medium,
  "ease-out motion-reduce:transition-none",
);

// Each page mounts its own layout, so a new one starts the rail where the last one left it and,
// when the two pages differ, moves it from there.
let previousCollapsed: boolean | undefined;

// How long a click may take to start a route change before an anticipated move is undone.
const NAVIGATION_START_GRACE_MS = 250;

export const CategoryRailCollapseProvider: React.FC<PropsWithChildren<{ collapsed: boolean }>> = ({
  collapsed: pageCollapsed,
  children,
}) => {
  const router = useRouter();
  const [settled, setSettled] = useState(() => previousCollapsed ?? pageCollapsed);
  const [anticipated, setAnticipated] = useState<boolean>();
  const routeChangePendingRef = useRef(false);
  const graceTimerRef = useRef<number | null>(null);
  const collapsed = anticipated ?? settled;

  // A page reached without a click on the rail (a link elsewhere, the browser's back button) still
  // moves the rail from where the last page left it. It paints there first, so the move has a
  // start to transition from.
  useEffect(() => {
    if (settled === pageCollapsed) return;
    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => setSettled(pageCollapsed));
    });
    return () => {
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, [pageCollapsed, settled]);

  useEffect(() => {
    previousCollapsed = collapsed;
  }, [collapsed]);

  useEffect(() => {
    const handleStart = () => {
      routeChangePendingRef.current = true;
    };
    const handleEnd = () => {
      routeChangePendingRef.current = false;
      setAnticipated(undefined);
    };
    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleEnd);
    router.events.on("routeChangeError", handleEnd);
    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleEnd);
      router.events.off("routeChangeError", handleEnd);
      if (graceTimerRef.current != null) window.clearTimeout(graceTimerRef.current);
    };
  }, [router.events]);

  const anticipate = useCallback((next: boolean) => {
    if (graceTimerRef.current != null) window.clearTimeout(graceTimerRef.current);
    setAnticipated(next);
    graceTimerRef.current = window.setTimeout(() => {
      graceTimerRef.current = null;
      if (!routeChangePendingRef.current) setAnticipated(undefined);
    }, NAVIGATION_START_GRACE_MS);
  }, []);

  const leaving = anticipated != null && anticipated !== pageCollapsed;
  const value = useMemo(
    () => ({ collapsed, anticipate, leaving }),
    [anticipate, collapsed, leaving],
  );

  return (
    <CategoryRailCollapseContext.Provider value={value}>
      {/* Rows dragged in the category sidebar can drop on the rail's icons. */}
      <CategoryRailRowDropProvider>{children}</CategoryRailRowDropProvider>
    </CategoryRailCollapseContext.Provider>
  );
};

/**
 * Keeps the top rail's contents clear of the workspace mark while the rail is collapsed. The page
 * column grows 64px to the left as the rail narrows, so this inset grows with it and the contents
 * stay in place.
 */
export const CategoryRailTopRailInset: React.FC<PropsWithChildren> = ({ children }) => {
  const { collapsed } = useCategoryRailCollapse();
  const ref = useRef<HTMLDivElement | null>(null);
  const paddingLeft = collapsed ? "64px" : "0px";
  useTransitionAcrossPages("category-rail:top-rail-inset", ref, "paddingLeft", paddingLeft);

  return (
    <div
      ref={ref}
      className={classNames(
        "h-full transition-[padding-left]",
        CATEGORY_RAIL_COLLAPSE_TRANSITION_CLASS,
      )}
      style={{ paddingLeft }}
    >
      {children}
    </div>
  );
};
